import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import { 
  Calendar, 
  ListTodo, 
  Filter, 
  Truck, 
  Package, 
  AlertTriangle, 
  ArrowRight, 
  Save, 
  CheckCircle2, 
  ShoppingCart,
  ChevronDown,
  ChevronUp,
  Info,
  History,
  Loader2,
  MapPin
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export default function WarehouseDispo() {
  const queryClient = useQueryClient();
  const [orderAmounts, setOrderAmounts] = useState({}); // { productId: amount }
  const [selectedIds, setSelectedIds] = useState(new Set()); // IDs of products selected for order

  // 1. Fetch Products
  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => base44.entities.Product.list(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Fetch existing purchase orders to know what's already ordered
  const { data: existingOrders = [] } = useQuery({
    queryKey: ['purchase_orders'],
    queryFn: () => base44.entities.PurchaseOrder.list(),
  });

  const orderedIds = useMemo(() => {
    return new Set(existingOrders.filter(o => o.status === 'bestellt').map(o => o.product_id));
  }, [existingOrders]);

  const saveOrderMutation = useMutation({
    mutationFn: async (orderData) => {
      return base44.entities.PurchaseOrder.createMany(orderData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['purchase_orders']);
      toast.success("Bestellung erfolgreich gespeichert.");
      setSelectedIds(new Set());
    },
    onError: (err) => toast.error("Fehler: " + err.message)
  });

  // 2. Identify Products with Order Need (BMV Logic)
  const dispoProducts = useMemo(() => {
    return products.filter(p => {
      const stock = p.stock || 0;
      const minStock = p.min_stock || 0;
      const meldeBestand = p.melde_bestand || 0;
      
      // Condition: stock = 0 OR stock <= melde_bestand OR stock <= min_stock
      return stock === 0 || stock <= meldeBestand || stock <= minStock;
    }).map(p => {
      const hoechst = p.hoechst_bestand || 100;
      const stock = p.stock || 0;
      const suggestion = Math.max(0, hoechst - stock);
      
      return {
        ...p,
        suggestedAmount: suggestion
      };
    });
  }, [products]);

  // 3. Group by Supplier
  const groupedBySupplier = useMemo(() => {
    const groups = {};
    
    dispoProducts.forEach(product => {
      let suppliers = product.suppliers || [];
      if (typeof suppliers === 'string') {
        try { suppliers = JSON.parse(suppliers); } catch { suppliers = []; }
      }
      
      const primarySupplier = suppliers.length > 0 ? suppliers[0].name : 'Kein Lieferant';
      
      if (!groups[primarySupplier]) {
        groups[primarySupplier] = {
          name: primarySupplier,
          products: []
        };
      }
      groups[primarySupplier].products.push(product);
    });
    
    return Object.values(groups);
  }, [dispoProducts]);

  const toggleSelect = (productId) => {
    const next = new Set(selectedIds);
    if (next.has(productId)) next.delete(productId);
    else next.add(productId);
    setSelectedIds(next);
  };

  const toggleSelectAll = (supplierProducts) => {
    const allIds = supplierProducts.map(p => p.id);
    const allSelected = allIds.every(id => selectedIds.has(id));
    
    const next = new Set(selectedIds);
    if (allSelected) {
      allIds.forEach(id => next.delete(id));
    } else {
      allIds.forEach(id => next.add(id));
    }
    setSelectedIds(next);
  };

  const handleAmountChange = (productId, val) => {
    setOrderAmounts(prev => ({
      ...prev,
      [productId]: val
    }));
  };

  const handleSaveOrder = (supplier) => {
    const selectedForThisSupplier = supplier.products.filter(p => selectedIds.has(p.id));
    
    if (selectedForThisSupplier.length === 0) {
      toast.error("Bitte wählen Sie mindestens ein Produkt aus.");
      return;
    }

    const orderData = selectedForThisSupplier.map(p => ({
      product_id: p.id,
      supplier_name: supplier.name,
      ordered_quantity: parseInt(orderAmounts[p.id] !== undefined ? orderAmounts[p.id] : p.suggestedAmount),
      status: 'bestellt'
    }));

    saveOrderMutation.mutate(orderData);
  };

  if (isLoading) {
    return (
      <div className="p-10 text-center text-slate-400">
        <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-blue-500" />
        <p>Lade Dispositionsliste...</p>
      </div>
    );
  }

  return (
    <PageShell
      head={(
          <div className="flex items-center gap-2 mb-2 justify-between">
            <div className="flex items-center gap-2">
              <PageHeader icon={<Calendar className="w-5 h-5" />} title="Dispo (Planlama)" />
            </div>
            <div className="flex gap-2">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-100 px-3 py-1">
                {dispoProducts.length} Artikel mit Bedarf
              </Badge>
              {orderedIds.size > 0 && (
                <Badge className="bg-emerald-600 text-white border-none px-3 py-1 font-bold">
                  {orderedIds.size} Aktiv Bestellt
                </Badge>
              )}
            </div>
          </div>
      )}
    >

      {/* Stats Quick View */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-white border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Gesamtbedarf</p>
            <div className="text-2xl font-black text-slate-900">{dispoProducts.length}</div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-100 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[10px] font-bold text-red-600 uppercase tracking-wider">Ausverkauft</p>
            <div className="text-2xl font-black text-red-900">{dispoProducts.filter(p => (p.stock || 0) === 0).length}</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-100 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Kritisch</p>
            <div className="text-2xl font-black text-amber-900">{dispoProducts.filter(p => (p.stock || 0) <= (p.min_stock || 0)).length}</div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-100 shadow-sm">
          <CardContent className="p-4">
            <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Aktiv Bestellt</p>
            <div className="text-2xl font-black text-emerald-900">{orderedIds.size}</div>
          </CardContent>
        </Card>
      </div>

      {/* Dispo List grouped by Supplier */}
      <div className="space-y-6">
        {groupedBySupplier.length === 0 ? (
          <Card className="border-2 border-dashed">
            <CardContent className="p-12 text-center text-slate-400">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-20 text-emerald-500" />
              <p className="font-medium">Alle Bestände sind optimal.</p>
              <p className="text-xs">Derzeit besteht kein automatischer Bestellbedarf.</p>
            </CardContent>
          </Card>
        ) : (
          groupedBySupplier.map((supplier) => (
            <div key={supplier.name} className="space-y-3">
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-amber-600" />
                  <h2 className="font-bold text-slate-800 uppercase tracking-tight text-sm">
                    Lieferant: {supplier.name}
                  </h2>
                  <Badge className="bg-slate-100 text-slate-600 text-[10px]">{supplier.products.length} Artikel</Badge>
                </div>
              </div>

              <Card className="overflow-hidden border-slate-200 shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="p-4 w-10 text-center">
                          <input 
                            type="checkbox" 
                            className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                            checked={supplier.products.length > 0 && supplier.products.every(p => selectedIds.has(p.id) || orderedIds.has(p.id))}
                            onChange={() => toggleSelectAll(supplier.products)}
                          />
                        </th>
                        <th className="text-left p-4 font-bold text-slate-500 uppercase text-[10px]">Artikel / Produktname</th>
                        <th className="text-center p-4 font-bold text-slate-500 uppercase text-[10px]">Bestand</th>
                        <th className="text-center p-4 font-bold text-slate-500 uppercase text-[10px]">Min / Melde / Max</th>
                        <th className="text-center p-4 font-bold text-blue-600 uppercase text-[10px]">Vorschlag (BMV)</th>
                        <th className="text-right p-4 font-bold text-slate-900 uppercase text-[10px] w-40">Tatsächliche Menge</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {supplier.products.map((product) => {
                        const currentAmount = orderAmounts[product.id] !== undefined 
                          ? orderAmounts[product.id] 
                          : product.suggestedAmount;
                        
                        const isOutOfStock = (product.stock || 0) === 0;
                        const isBelowMin = (product.stock || 0) <= (product.min_stock || 0);
                        const isSelected = selectedIds.has(product.id);
                        const isOrdered = orderedIds.has(product.id);

                        return (
                          <tr key={product.id} className={cn(
                            "transition-all group",
                            isOrdered ? "bg-emerald-50/60" : isSelected ? "bg-blue-50/40" : "hover:bg-slate-50/50"
                          )}>
                            <td className="p-4 text-center">
                              <input 
                                type="checkbox" 
                                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                                checked={isSelected || isOrdered}
                                onChange={() => toggleSelect(product.id)}
                                disabled={isOrdered}
                              />
                            </td>
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg border bg-white flex items-center justify-center shrink-0 overflow-hidden shadow-sm">
                                  {product.image_url ? (
                                    <img loading="lazy" decoding="async" src={product.image_url} alt="" className="w-full h-full object-contain" />
                                  ) : (
                                    <Package className="w-5 h-5 text-slate-300" />
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className={cn("font-bold text-slate-900 truncate leading-tight", isOrdered && "text-emerald-800")}>{product.name}</p>
                                    {isOrdered && (
                                      <Badge className="bg-emerald-600 text-white text-[8px] font-black h-4 px-1.5 uppercase border-none">
                                        <CheckCircle2 className="w-2.5 h-2.5 mr-1" />
                                        Bestellt
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest mt-0.5">{product.sku || 'KEINE SKU'}</p>
                                  {product.lagerort && (
                                    <p className="text-[10px] text-slate-600 mt-1 inline-flex items-center gap-1 bg-slate-100 px-1.5 py-0.5 rounded font-mono">
                                      <MapPin className="w-2.5 h-2.5" />
                                      {product.lagerort}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="p-4 text-center">
                              <div className="flex flex-col items-center">
                                <span className={cn(
                                  "text-sm font-black",
                                  isOutOfStock ? "text-red-600" : isBelowMin ? "text-amber-600" : "text-slate-900"
                                )}>
                                  {product.stock || 0}
                                </span>
                                <span className="text-[9px] text-slate-400 uppercase font-bold">{product.unit || 'Stk'}</span>
                              </div>
                            </td>
                            <td className="p-4 text-center">
                              <div className="flex items-center justify-center gap-2 text-[11px] font-bold">
                                <span className="text-red-500" title="Mindestbestand">{product.min_stock || 0}</span>
                                <span className="text-slate-300">/</span>
                                <span className="text-amber-600" title="Meldebestand">{product.melde_bestand || 0}</span>
                                <span className="text-slate-300">/</span>
                                <span className="text-slate-500" title="Höchstbestand">{product.hoechst_bestand || 0}</span>
                              </div>
                            </td>
                            <td className="p-4 text-center">
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 font-black text-sm px-3 py-0.5 border-blue-100">
                                {product.suggestedAmount}
                              </Badge>
                            </td>
                            <td className="p-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <Input 
                                  type="number" 
                                  disabled={isOrdered}
                                  className={cn(
                                    "w-24 h-10 text-center font-black text-lg border-slate-300",
                                    isOrdered ? "bg-emerald-100 border-emerald-300 text-emerald-800" : "focus:border-blue-500 focus:ring-blue-500"
                                  )}
                                  value={currentAmount}
                                  onChange={(e) => handleAmountChange(product.id, e.target.value)}
                                />
                                {!isOrdered && currentAmount !== product.suggestedAmount && (
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-8 w-8 text-slate-400 hover:text-blue-600"
                                    onClick={() => handleAmountChange(product.id, product.suggestedAmount)}
                                    title="Vorschlag wiederherstellen"
                                  >
                                    <ShoppingCart className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="bg-slate-50 p-3 border-t flex justify-between items-center">
                  <div className="flex items-center gap-2 text-[10px] text-slate-500 font-medium">
                    <Info className="w-3.5 h-3.5" />
                    <span>Markieren Sie Produkte als bestellt, um sie im 'Eingang' Tab zu sehen.</span>
                  </div>
                  <Button 
                    size="sm" 
                    disabled={saveOrderMutation.isPending}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2 font-bold uppercase text-[10px] px-6 shadow-md"
                    onClick={() => handleSaveOrder(supplier)}
                  >
                    {saveOrderMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    Als 'Bestellt' Markieren
                  </Button>
                </div>
              </Card>
            </div>
          ))
        )}
      </div>
    </PageShell>
  );
}
